import React, { useState, useEffect } from 'react'
import InfoButton from '../../components/InfoButton'
import TariffUpgradePopup from './TariffUpgradePopup'
import ConfirmationPopup from '../Common/ConfirmationPopup'
import SubscriptionArchiveInfoPopup from './SubscriptionArchiveInfoPopup'
import ActivePackagesPopup from './ActivePackagesPopup'
import { TariffUpgradeType } from '../../methods/TariffUpgradeType'
import { httpClient } from '../../funcs'
import { notifyAboutError } from '../../methods/notifyUser'
import { hasProSubscription } from '../../methods/hasProSubscription'
import { ReactComponent as Warning } from '../Icons/size24/Warning.svg'
import {
    AvailableUpgradeTag,
    PurchasedUpgradeTag,
    UpgradeTagsRow,
    isPartialUpgrade,
    upgradeTypeOf,
} from './UpgradeTag'
import "pages/Styles/MyTariffV2.sass"

const SUBSCRIPTION_RENEWAL_INVOICE_STORAGE_KEY = 'subscription_archive_renewal_invoice_v1'

function renewalInvoiceStorageKey(
    userId: string | undefined,
    tariffId: unknown,
    subscriptionExpiresAt: string | null | undefined,
    price: unknown
): string | null {
    if (!userId || tariffId === undefined || tariffId === null) {
        return null
    }
    return `${userId}:${String(tariffId)}:${subscriptionExpiresAt || ''}:${String(price || '')}`
}

function readRenewalInvoiceRequested(key: string | null): boolean {
    if (!key || typeof localStorage === 'undefined') {
        return false
    }
    try {
        const raw = localStorage.getItem(SUBSCRIPTION_RENEWAL_INVOICE_STORAGE_KEY)
        if (!raw) {
            return false
        }
        const map = JSON.parse(raw) as Record<string, boolean>
        return !!map[key]
    } catch {
        return false
    }
}

function persistRenewalInvoiceRequested(key: string | null) {
    if (!key || typeof localStorage === 'undefined') {
        return
    }
    try {
        const raw = localStorage.getItem(SUBSCRIPTION_RENEWAL_INVOICE_STORAGE_KEY)
        const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
        map[key] = true
        localStorage.setItem(SUBSCRIPTION_RENEWAL_INVOICE_STORAGE_KEY, JSON.stringify(map))
    } catch {
        // ignore quota / private mode
    }
}

interface ProSubscriptionStatus {
    active?: boolean
    is_pro_subscription?: boolean
    next_charge_date?: string | null
    active_until?: string | null
    wont_renew?: boolean
}

interface MyTariffV2Props {
    currentTariff: any
    proSubscriptionStatus?: ProSubscriptionStatus | null
    $store: any
    $commitToStore: (data: any) => void
    onLoadTariffs?: () => void
}

function MyTariffV2({ currentTariff, proSubscriptionStatus, $store, $commitToStore, onLoadTariffs }: MyTariffV2Props) {
    const userId = $store?.userSession?._id != null ? String($store.userSession._id) : undefined
    const [showInfoPopup, setShowInfoPopup] = useState(false)
    const [showUpgradePopup, setShowUpgradePopup] = useState(false)
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
    const [availableUpgrades, setAvailableUpgrades] = useState<TariffUpgradeType[]>([])
    const [cards, setCards] = useState<any[]>([])
    const [cardsLoading, setCardsLoading] = useState(false)
    const [cardsLoaded, setCardsLoaded] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [renewalInvoiceAlreadyRequested, setRenewalInvoiceAlreadyRequested] = useState(false)

    // Определяем доступные для докупки апгрейды (для подписки докупка недоступна)
    useEffect(() => {
        if (currentTariff?.is_subscription) {
            setAvailableUpgrades([])
            return
        }
        let purchasedUpgradeTypes: string[] = []
        if (currentTariff?.active_upgrades?.length) {
            purchasedUpgradeTypes = currentTariff.active_upgrades.map((upgrade: any) =>
                upgradeTypeOf(upgrade)
            )
        }
        const available = TariffUpgradeType.availableUpgradesForJurisdiction(
            $store?.userSession?.jurisdiction,
        ).filter(
            upgrade => !purchasedUpgradeTypes.includes(upgrade)
        )
        setAvailableUpgrades(available)
    }, [currentTariff, $store?.userSession?.jurisdiction])

    useEffect(() => {
        if (showInfoPopup && currentTariff?.is_subscription && !cardsLoaded) {
            loadCards()
        }
    }, [showInfoPopup, currentTariff, cardsLoaded])

    useEffect(() => {
        if (!currentTariff?.is_subscription || !userId || !currentTariff.tariff?._id) {
            setRenewalInvoiceAlreadyRequested(false)
            return
        }
        const key = renewalInvoiceStorageKey(
            userId,
            currentTariff.tariff._id,
            currentTariff.subscription_expires_at,
            currentTariff.tariff.price
        )
        setRenewalInvoiceAlreadyRequested(readRenewalInvoiceRequested(key))
    }, [userId, currentTariff])

    const loadCards = async () => {
        setCardsLoading(true)
        try {
            const { data } = await httpClient.get('/user/entrepreneur/list_cards')
            setCards(Array.isArray(data) ? data : [])
            setCardsLoaded(true)
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        } finally {
            setCardsLoading(false)
        }
    }

    const isProActive = Boolean(
        proSubscriptionStatus?.active
        ?? proSubscriptionStatus?.is_pro_subscription
        ?? hasProSubscription($store?.userSession)
    )

    const formatExpirationDate = (expiresAt: string) => {
        const date = new Date(expiresAt)
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    const renderProSubscriptionBadge = (compact = false) => (
        <div className={`pro-subscription-badge${compact ? ' pro-subscription-badge--compact' : ''}`}>
            <span className="pro-subscription-badge__star" aria-hidden>★</span>
            <span className="pro-subscription-badge__text">Pro</span>
        </div>
    )

    const renderProBanner = () => {
        if (!isProActive) {
            return null
        }

        const nextChargeDate = proSubscriptionStatus?.next_charge_date
        const activeUntil = proSubscriptionStatus?.active_until
        const displayUntil = nextChargeDate || activeUntil
        const wontRenew = Boolean(proSubscriptionStatus?.wont_renew)
        const nextChargeCaption = displayUntil
            ? wontRenew
                ? `Действует до: ${formatExpirationDate(String(displayUntil))} · автопродление отключено`
                : `Следующее списание: ${formatExpirationDate(String(displayUntil))}`
            : 'Брендирование страницы подписания и расширенные возможности'

        return (
            <div className="current-tariff-card__pro-banner">
                {renderProSubscriptionBadge()}
                <div className="current-tariff-card__pro-banner-text">
                    <span className="current-tariff-card__pro-banner-title">Подписка Pro активна</span>
                    <span className="current-tariff-card__pro-banner-sub">{nextChargeCaption}</span>
                </div>
            </div>
        )
    }

    if (!currentTariff) {
        return (
            <div className="current-tariff-card">
                {renderProBanner()}
                <div className="no-tariff-state">
                    <div className="no-tariff-text">У вас нет активного тарифа</div>
                    <div className="no-tariff-subtitle">Выберите тариф ниже для начала работы</div>
                </div>
            </div>
        )
    }

    const { tariff, active_upgrades, total_balance, nearest_expiring_package, is_subscription, subscription_wont_renew, subscription_expires_at } = currentTariff

    const handleInfoClick = () => {
        setShowInfoPopup(true)
    }

    const handleRenewalBillCreated = () => {
        const key = renewalInvoiceStorageKey(
            userId,
            tariff._id,
            subscription_expires_at,
            tariff.price
        )
        persistRenewalInvoiceRequested(key)
        setRenewalInvoiceAlreadyRequested(true)
    }

    const handleCancelSubscription = async () => {
        if (cancelLoading) {
            return
        }
        setCancelLoading(true)
        try {
            await httpClient.put('/user/entrepreneur/cancel-subscription')
            $commitToStore({
                notification: {
                    title: 'Подписка отменена',
                    content: 'Безлимитный тариф будет действовать до конца оплаченного периода'
                }
            })
            setShowCancelConfirmation(false)
            setShowInfoPopup(false)
            onLoadTariffs?.()
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        } finally {
            setCancelLoading(false)
        }
    }

    const renderExpirationInfo = () => {
        // Для подписки используем subscription_expires_at
        if (is_subscription && subscription_expires_at) {
            const expirationDate = formatExpirationDate(subscription_expires_at)
            return (
                <div className="expiration-info">
                    <span>Действует до: {expirationDate}</span>
                </div>
            )
        }
        
        // Для обычных пакетов используем nearest_expiring_package
        if (!nearest_expiring_package) {
            return null
        }

        const expirationDate = formatExpirationDate(nearest_expiring_package.expires_at)
        return (
            <div className="expiration-info">
                <span>Действует до: {expirationDate}</span>
            </div>
        )
    }

    const renderBalanceText = () => {
        if (is_subscription || total_balance === -1) {
            return <span>Баланс: Безлимит</span>
        }
        return <span>Баланс: {total_balance}</span>
    }

    const getSubscriptionRenewalItems = () => {
        return [{
            type: 'subscription',
            _id: String(tariff._id),
            name: tariff.name || 'ОкиДоки',
            period: tariff.period,
            price: tariff.price || 0,
            quantity: 1,
            tariff_upgrades: (active_upgrades || []).map((upgrade: any) =>
                upgradeTypeOf(upgrade)
            ).filter(Boolean),
            upgrade_additional_price: tariff.upgrade_additional_price || {},
            base_price: tariff.base_price,
            subscription_price_level: tariff.subscription_price_level,
            subscription_price_level_change: tariff.subscription_price_level_change,
        }]
    }

    const renderSubscriptionWarning = () => {
        // Плашка только если подписка не будет продлена (prepaid === 0 и действует subscription_active_until)
        if (!is_subscription || !subscription_wont_renew) {
            return null
        }

        return (
            <div className="subscription-warning">
                <Warning className="subscription-warning__icon" />
                <div className="subscription-warning__body">
                    <div className="subscription-warning__title">
                        Подписка не будет продлена
                    </div>
                    <div className="subscription-warning__text">
                        Это старый тариф, который не будет автоматически продлен. По завершении действия подписки вам необходимо будет выбрать новый тариф, так как безлимитных тарифов больше не будет.
                    </div>
                </div>
            </div>
        )
    }

    const renderUpgrades = () => {
        const purchasedUpgrades = active_upgrades || []
        const showAvailable = !is_subscription
        const hasPurchasedUpgrades = purchasedUpgrades.length > 0
        const hasAvailableUpgrades = showAvailable && availableUpgrades.length > 0
        if (!hasPurchasedUpgrades && !hasAvailableUpgrades) {
            return null
        }

        return (
            <div className="current-upgrades-section">
                <UpgradeTagsRow>
                    {purchasedUpgrades.map((upgrade: any, index: number) => (
                        <PurchasedUpgradeTag
                            key={`purchased-${upgradeTypeOf(upgrade)}-${index}`}
                            upgrade={upgrade}
                            onPartialClick={() => setShowInfoPopup(true)}
                        />
                    ))}
                    {showAvailable && availableUpgrades.map((upgrade) => (
                        <AvailableUpgradeTag
                            key={`available-${upgrade}`}
                            upgradeType={upgrade}
                            onClick={() => setShowUpgradePopup(true)}
                        />
                    ))}
                </UpgradeTagsRow>
            </div>
        )
    }

    const expirationInfo = renderExpirationInfo()

    return (
        <>
            <div className="current-tariff-card">
                {renderProBanner()}
                <div className="tariff-header">
                    <div className="tariff-header__title-row">
                        <div className="tariff-name">Текущий тариф: {tariff.name}</div>
                    </div>
                    <div className="tariff-balance-info">
                        <div className="balance-text">
                            {renderBalanceText()}
                            {expirationInfo && (
                                <>
                                    <span className="bullet-point">•</span>
                                    {expirationInfo}
                                </>
                            )}
                        </div>
                        <InfoButton onClick={handleInfoClick} size="medium" />
                    </div>
                </div>
                {renderSubscriptionWarning()}
                {renderUpgrades()}
            </div>

            {showInfoPopup && is_subscription && (
                <SubscriptionArchiveInfoPopup
                    onClose={() => setShowInfoPopup(false)}
                    tariff={tariff}
                    subscriptionExpiresAt={subscription_expires_at}
                    subscriptionWontRenew={subscription_wont_renew}
                    cards={cards}
                    cardsLoading={cardsLoading}
                    cancelLoading={cancelLoading}
                    renewalInvoiceAlreadyRequested={renewalInvoiceAlreadyRequested}
                    renewalItems={getSubscriptionRenewalItems()}
                    $store={$store}
                    $commitToStore={$commitToStore}
                    onLoadTariffs={onLoadTariffs}
                    onCancelClick={() => setShowCancelConfirmation(true)}
                    onRenewalBillCreated={handleRenewalBillCreated}
                />
            )}

            {showInfoPopup && !is_subscription && (
                <ActivePackagesPopup
                    onClose={() => setShowInfoPopup(false)}
                    $store={$store}
                    $commitToStore={$commitToStore}
                />
            )}

            {showUpgradePopup && (
                <TariffUpgradePopup
                    isOpen={showUpgradePopup}
                    onClose={() => setShowUpgradePopup(false)}
                    currentUpgrades={(active_upgrades || [])
                        .filter((upgrade: any) => !isPartialUpgrade(upgrade))
                        .map((upgrade: any) => upgradeTypeOf(upgrade))
                    }
                    onUpgradeSuccess={() => {
                        setShowUpgradePopup(false)
                        // TODO: Обновить данные тарифа
                    }}
                />
            )}

            {showCancelConfirmation && (
                <ConfirmationPopup
                    title="Отменить подписку?"
                    subtitle="Безлимитная подписка является архивным тарифом. После отмены автопродления вернуть эту подписку будет нельзя."
                    confirmText={cancelLoading ? 'Отменяем...' : 'Отменить подписку'}
                    cancelText="Оставить подписку"
                    onClose={() => setShowCancelConfirmation(false)}
                    onConfirm={handleCancelSubscription}
                    subtitleAlign="left"
                    zIndex={10001}
                />
            )}
        </>
    )
}

export default MyTariffV2
